import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { JourneyTagState, JourneyTagEntity } from './types';
import { DataPayload, SortPayloadAction } from '../types';
import { AxiosInstance } from 'axios';
import JourneyTagsApi from '../../api/journeyTagsApi';
import { EntityState } from '../types';

const initialState: JourneyTagState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 8,
  totalPages: 1,
  sortColumn: 'name',
  sortDirection: -1,
  selectedFilters: {},
};

const tagsSlice = createSlice({
  name: 'journeyTags',
  initialState,
  reducers: {
    setJourneyTags: (
      state,
      { payload }: PayloadAction<DataPayload<JourneyTagEntity>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    appendJourneyTags: (
      state,
      { payload }: PayloadAction<DataPayload<JourneyTagEntity>>
    ) => {
      state.data = [...state.data, ...payload.entities];
      state.totalPages = payload.totalPages;
    },
    sortJourneyTags: (
      state,
      { payload }: SortPayloadAction<JourneyTagEntity>
    ) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
      state.currentPage = initialState.currentPage;
      state.data = initialState.data;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setSearchFilter: (state, { payload }: PayloadAction<string>) => {
      state.selectedFilters = {
        ...state.selectedFilters,
        search: payload,
      };
      state.currentPage = initialState.currentPage;
      state.data = initialState.data;
    },
    resetJourneyTagState: () => initialState,
  },
});

export const loadTags = async (
  axios: AxiosInstance,
  companyId?: string,
  state?: EntityState<JourneyTagEntity>
) => {
  if (!companyId) {
    return setJourneyTags({ entities: [], totalPages: 0 });
  }

  if (!state) {
    state = initialState;
  }

  const response = await JourneyTagsApi.getJourneyTags(axios, companyId, {
    limit: state.resultsPerPage,
    page: state.currentPage,
    sortColumn: state.sortColumn,
    sortDirection: state.sortDirection as 1 | -1,
    search: state.selectedFilters?.search,
  });

  return setJourneyTags(response);
};

export const loadMoreTags = async (
  axios: AxiosInstance,
  companyId?: string,
  state?: EntityState<JourneyTagEntity>
) => {
  if (!companyId) {
    return appendJourneyTags({ entities: [], totalPages: 0 });
  }

  if (!state) {
    state = initialState;
  }

  const response = await JourneyTagsApi.getJourneyTags(axios, companyId, {
    limit: state.resultsPerPage,
    page: state.currentPage,
    sortColumn: state.sortColumn,
    sortDirection: state.sortDirection as 1 | -1,
    search: state.selectedFilters?.search,
  });

  return appendJourneyTags(response);
};

export const {
  setJourneyTags,
  appendJourneyTags,
  sortJourneyTags,
  setCurrentPage,
  setSearchFilter,
  resetJourneyTagState,
} = tagsSlice.actions;

export default tagsSlice.reducer;
